import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RadioTower, RefreshCw, AlertTriangle, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import AdminButtonGroup from '@/components/AdminButtonGroup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Oct 2026 — Massive API deprecation watch. Massive sunset
// /vX/reference/financials with only RFC 8594 headers as notice; this card
// lists every beta (vX) endpoint we call plus any endpoint that has sent
// Deprecation / Sunset / 410, so the switch happens before the card goes
// blank again.
const STATUS = {
  gone:          { label: 'GONE (410)',     cls: 'bg-rose-600 text-white' },
  sunset_passed: { label: 'SUNSET PASSED',  cls: 'bg-rose-500/20 text-rose-700 dark:text-rose-300 ring-1 ring-rose-500/50' },
  deprecated:    { label: 'DEPRECATED',     cls: 'bg-amber-500/20 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500/50' },
  beta:          { label: 'BETA · no notice', cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 ring-1 ring-sky-500/40' },
  ok:            { label: 'OK',             cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
};

function Row({ r }) {
  const s = STATUS[r.status] || STATUS.ok;
  const ago = r.last_seen ? Math.max(0, Math.round((Date.now() - new Date(r.last_seen).getTime()) / 3600000)) : null;
  return (
    <tr className="border-t border-border/50 align-top" data-testid={`massive-watch-row-${r.endpoint.replace(/[^a-z0-9]+/gi, '-')}`}>
      <td className="py-1 pr-2 font-mono text-[11px] break-all">{r.endpoint}</td>
      <td className="py-1 pr-2"><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase whitespace-nowrap ${s.cls}`}>{s.label}</span></td>
      <td className="py-1 pr-2 font-mono text-[10px] whitespace-nowrap">{r.sunset || '—'}</td>
      <td className="py-1 pr-2 text-[10px] break-all">
        {r.successor ? <a href={r.successor} target="_blank" rel="noopener noreferrer" className="text-cyan-700 dark:text-cyan-300 hover:underline inline-flex items-center gap-1">{r.successor.replace(/^https?:\/\//, '')} <ExternalLink className="w-2.5 h-2.5" /></a> : '—'}
      </td>
      <td className="py-1 font-mono text-[10px] text-muted-foreground whitespace-nowrap" title={`last status ${r.last_status ?? '—'} · ${r.hits} calls · ${r.hits_410} × 410`}>
        {r.hits} calls{ago != null ? ` · ${ago}h ago` : ''}{r.hits_410 ? ` · ${r.hits_410}×410` : ''}
      </td>
    </tr>
  );
}

export default function AdminMassiveApiWatchCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/massive/deprecations`, { withCredentials: true });
      setData(d);
    } catch { /* admin-only; ignore */ }
  };
  useEffect(() => { if (user?.role === 'admin') load(); }, [user?.role]);

  if (user?.role !== 'admin') return null;

  const probe = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/admin/massive/deprecations/probe`, {}, { withCredentials: true, timeout: 90000 });
      const flagged = (d.probed || []).filter((p) => p.deprecation || p.sunset || p.status === 410);
      toast[flagged.length ? 'warning' : 'success'](`Probed ${d.probed.length} endpoints`, {
        description: flagged.length ? `${flagged.length} carry a deprecation notice: ${flagged.map((f) => f.endpoint).join(', ')}` : 'No deprecation or sunset headers on any endpoint we use.',
      });
      await load();
    } catch (err) {
      toast.error('Probe failed', { description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(false);
    }
  };

  const alerts = data?.alerts || 0;
  const badge = data ? (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${alerts ? 'bg-rose-600 text-white' : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'}`}
      data-testid="massive-watch-badge"
    >
      {alerts ? `${alerts} notice${alerts > 1 ? 's' : ''}` : `${data.rows.length} watched · clear`}
    </span>
  ) : null;

  return (
    <AdminButtonGroup
      icon={<RadioTower className="w-3.5 h-3.5" />} label="Massive API watch" cadence="passive · every call"
      tone={alerts ? 'rose' : 'sky'} testid="admin-group-massive-watch"
      storageKey="dividrip:admin-group-massive-watch"
      statusBadge={badge}
      defaultOpen={alerts > 0}
    >
      <div className="w-full col-span-full min-w-[min(100vw-2rem,900px)] space-y-2" data-testid="massive-watch-card">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[11px] text-muted-foreground leading-snug max-w-2xl">
            Every Massive endpoint we call is checked for <span className="font-mono">Deprecation</span> / <span className="font-mono">Sunset</span> headers and 410s — the only notice Massive gave before killing <span className="font-mono">/vX/reference/financials</span>.
            Beta (<span className="font-mono">vX</span>) endpoints are listed even with no notice so you can see what's exposed.
          </p>
          <button
            type="button" onClick={probe} disabled={busy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-60"
            data-testid="massive-watch-probe"
            title="Re-request each known endpoint once (limit=1) so the headers are fresh"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Probe now
          </button>
        </div>
        {alerts > 0 && (
          <div className="flex items-start gap-2 rounded-md bg-rose-500/10 border border-rose-500/40 px-3 py-2 text-xs text-rose-800 dark:text-rose-200" data-testid="massive-watch-alert">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>{alerts} endpoint{alerts > 1 ? 's have' : ' has'} a deprecation or sunset notice — migrate to the successor before the sunset date.</div>
          </div>
        )}
        {data ? (
          data.rows.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[9px] uppercase tracking-wide text-muted-foreground">
                    <th className="pr-2 font-semibold">Endpoint</th><th className="pr-2 font-semibold">Status</th><th className="pr-2 font-semibold">Sunset</th><th className="pr-2 font-semibold">Successor</th><th className="font-semibold">Traffic</th>
                  </tr>
                </thead>
                <tbody>{data.rows.map((r) => <Row key={r.endpoint} r={r} />)}</tbody>
              </table>
            </div>
          ) : <p className="text-[11px] text-muted-foreground">No beta endpoints or notices recorded yet — fills in as the crons and pages make calls.</p>
        ) : <p className="text-[11px] text-muted-foreground">Loading…</p>}
      </div>
    </AdminButtonGroup>
  );
}
